import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaApple, FaAndroid, FaShieldAlt, FaBolt, FaQrcode } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

// Update these when a new release is uploaded to the server (see the
// deployment note in this app's README/ops docs for where the APK itself
// actually lives -- this component only links to it, it doesn't host it).
const APK_VERSION = '1.0.0';
const APK_SIZE_LABEL = '~115 MB';
const APK_DOWNLOAD_PATH = '/downloads/sheba-smart-home.apk';
const APK_MIN_ANDROID = '7.0';

const AppDownloadSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const downloadUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${APK_DOWNLOAD_PATH}`
      : APK_DOWNLOAD_PATH;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&color=10-14-39&bgcolor=255-255-255&data=${encodeURIComponent(downloadUrl)}`;

  const highlights = [
    { icon: FaBolt, text: t('appDownload.highlight1') },
    { icon: FaShieldAlt, text: t('appDownload.highlight2') },
    { icon: FaQrcode, text: t('appDownload.highlight3') },
  ];

  return (
    <section
      id="download"
      ref={ref}
      className="section-padding bg-gradient-to-b from-[#0a0e27] to-[#0f1640] relative overflow-hidden"
      data-testid="app-download-section"
    >
      {/* Ambient glow blobs, same language as HeroSection's background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-[#00d4ff]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#0099ff]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: copy + CTAs */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#00d4ff] text-sm font-semibold tracking-wide mb-5">
              {t('appDownload.badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              <span className="gradient-text">{t('appDownload.title')}</span>
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-xl">
              {t('appDownload.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <a
                href={downloadUrl}
                download
                className="btn-primary flex items-center justify-center gap-3 glow-hover"
                data-testid="android-download-btn"
              >
                <FaAndroid size={20} />
                <span className="flex flex-col items-start leading-tight normal-case">
                  <span className="text-[11px] opacity-80 tracking-normal">{t('appDownload.androidEyebrow')}</span>
                  <span>{t('appDownload.androidButton')}</span>
                </span>
              </a>

              <button
                type="button"
                disabled
                className="flex items-center justify-center gap-3 px-8 py-[14px] rounded-full border-2 border-white/15 text-white/40 cursor-not-allowed"
                data-testid="ios-download-btn"
              >
                <FaApple size={20} />
                <span className="flex flex-col items-start leading-tight normal-case">
                  <span className="text-[11px] opacity-70 tracking-normal">{t('appDownload.iosEyebrow')}</span>
                  <span className="font-semibold text-sm">{t('appDownload.iosButton')}</span>
                </span>
              </button>
            </div>

            <p className="text-gray-500 text-sm mb-8">
              {t('appDownload.meta', {
                version: APK_VERSION,
                size: APK_SIZE_LABEL,
                android: APK_MIN_ANDROID,
              })}
            </p>

            <div className="grid sm:grid-cols-3 gap-4">
              {highlights.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="glass rounded-2xl p-4 flex items-start gap-3"
                >
                  <h.icon className="text-[#00d4ff] mt-0.5 shrink-0" size={18} />
                  <span className="text-gray-300 text-sm">{h.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: QR card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="flex justify-center md:justify-end"
          >
            <div className="glass-strong rounded-3xl p-8 w-full max-w-sm text-center relative overflow-hidden">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#00d4ff]/10 to-[#0099ff]/10 pointer-events-none" />
              <div className="relative z-10">
                <div className="bg-white rounded-2xl p-3 inline-block mb-5">
                  <img
                    src={qrSrc}
                    alt={t('appDownload.qrAlt')}
                    width={180}
                    height={180}
                    className="rounded-lg"
                    loading="lazy"
                  />
                </div>
                <p className="text-white font-semibold mb-1">{t('appDownload.scanTitle')}</p>
                <p className="text-gray-400 text-sm mb-5">{t('appDownload.scanSubtitle')}</p>
                <div className="h-px bg-white/10 mb-5" />
                <p className="text-gray-400 text-xs leading-relaxed">
                  {t('appDownload.installHelp')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AppDownloadSection;
